import { FC, ReactNode } from 'react';
import {
  ACWR_ZONES,
  ACWR_ACUTE_DAYS,
  ACWR_CHRONIC_DAYS,
  ACWR_MIN_HISTORY_DAYS,
  SRPE_BINS,
  RI_THRESHOLDS,
  MONOTONY_WARN_THRESHOLD,
  QUESTIONNAIRE_COVERAGE_BANDS,
  DELTA_WARN_PCT,
  DELTA_ALERT_PCT,
} from '@leopart/calc';
import { InfoDrawerValue } from '../providers/InfoDrawer';
import styles from './domainGuides.module.scss';

/**
 * Guide di dominio per l'InfoDrawer condiviso (feature #16): spiegazioni dei
 * concetti coach-facing (ACWR, sRPE, RI, monotonia, Δ%, copertura) da mostrare
 * col bottone «?» accanto alle metriche. I NUMERI delle soglie sono interpolati
 * da @leopart/calc (fonte unica) — se una soglia cambia lì, la guida la segue.
 * Nessuna soglia riscritta a mano.
 */

interface Band {
  dot: string;
  range: string;
  band: string;
}

const Scale: FC<{ bands: Band[] }> = ({ bands }) => (
  <div className={styles.scale}>
    {bands.map((f) => (
      <div key={f.range + f.band} className={styles.scaleRow}>
        <span className={`${styles.dot} ${f.dot}`} />
        <span className={styles.range}>{f.range}</span>
        <span className={styles.band}>{f.band}</span>
      </div>
    ))}
  </div>
);

const Guide: FC<{ children: ReactNode }> = ({ children }) => (
  <div className={styles.guide}>{children}</div>
);

export const DOMAIN_GUIDES = {
  acwr: {
    title: 'ACWR — rapporto carico acuto/cronico',
    placement: 'end',
    size: 'md',
    description: (
      <Guide>
        <p className={styles.p}>
          L&apos;ACWR confronta il carico dell&apos;ultima settimana (acuto,{' '}
          {ACWR_ACUTE_DAYS} giorni) con la media delle ultime settimane (cronico,{' '}
          {ACWR_CHRONIC_DAYS} giorni). Dice se l&apos;atleta sta aumentando il
          carico troppo in fretta rispetto a quanto è abituato.
        </p>
        <div className={styles.sectionTitle}>Come leggerlo</div>
        <Scale
          bands={[
            { dot: styles.dotGrey, range: `< ${ACWR_ZONES.okMin}`, band: 'poco allenante (detraining)' },
            { dot: styles.dotGreen, range: `${ACWR_ZONES.okMin}–${ACWR_ZONES.okMax}`, band: 'zona ottimale' },
            { dot: styles.dotAmber, range: `${ACWR_ZONES.okMax}–${ACWR_ZONES.warnMax}`, band: 'attenzione' },
            { dot: styles.dotRed, range: `> ${ACWR_ZONES.warnMax}`, band: 'rischio (carico cresciuto troppo)' },
          ]}
        />
        <p className={styles.note}>
          Sopra {ACWR_ZONES.warnMax} il carico è salito troppo in fretta →
          rischio infortunio; sotto {ACWR_ZONES.okMin} è troppo poco →
          decondizionamento. Servono almeno {ACWR_MIN_HISTORY_DAYS} giorni di
          storico perché il valore sia affidabile.
        </p>
      </Guide>
    ),
  },

  srpe: {
    title: 'sRPE — carico interno (AU)',
    placement: 'end',
    size: 'md',
    description: (
      <Guide>
        <p className={styles.p}>
          Il carico interno di una seduta si misura moltiplicando lo sforzo
          percepito (RPE, scala 1–10) per i minuti: sforzo × durata = unità
          arbitrarie (AU), metodo di Foster. È «quanto è pesata» la seduta per
          l&apos;atleta, non i minuti in campo.
        </p>
        <div className={styles.sectionTitle}>Fasce (AU/giorno)</div>
        <Scale
          bands={[
            { dot: styles.dotBin0, range: `≤ ${SRPE_BINS[0]}`, band: 'leggero' },
            { dot: styles.dotBin1, range: `${SRPE_BINS[0] + 1}–${SRPE_BINS[1]}`, band: 'moderato' },
            { dot: styles.dotBin2, range: `${SRPE_BINS[1] + 1}–${SRPE_BINS[2]}`, band: 'impegnativo' },
            { dot: styles.dotBin3, range: `${SRPE_BINS[2] + 1}–${SRPE_BINS[3]}`, band: 'alto' },
            { dot: styles.dotBin4, range: `> ${SRPE_BINS[3]}`, band: 'molto alto' },
          ]}
        />
        <p className={styles.note}>
          La heatmap colora ogni giorno per fascia; la colonna «Sett.» è la somma
          settimanale, cioè il carico totale della squadra.
        </p>
      </Guide>
    ),
  },

  ri: {
    title: 'RI — indice di recupero',
    placement: 'end',
    size: 'md',
    description: (
      <Guide>
        <p className={styles.p}>
          Il Recovery Index (RI) è un valore da 0 a 100 dello stato di recupero
          dell&apos;atleta, combinando le componenti mentale, muscolare, cardio e
          strain. Più è alto, meglio l&apos;atleta è recuperato.
        </p>
        <div className={styles.sectionTitle}>Come leggerlo</div>
        <Scale
          bands={[
            { dot: styles.dotRiGood, range: `≥ ${RI_THRESHOLDS.good}`, band: 'buono' },
            { dot: styles.dotRiNormal, range: `${RI_THRESHOLDS.normal}–${RI_THRESHOLDS.good - 1}`, band: 'normale' },
            { dot: styles.dotRiWarning, range: `${RI_THRESHOLDS.warning}–${RI_THRESHOLDS.normal - 1}`, band: 'attenzione' },
            { dot: styles.dotRiCritical, range: `< ${RI_THRESHOLDS.warning}`, band: 'critico' },
          ]}
        />
        <p className={styles.note}>
          Sotto {RI_THRESHOLDS.normal} l&apos;atleta non ha recuperato: valuta di
          alleggerire il carico o approfondire con il dettaglio atleta.
        </p>
      </Guide>
    ),
  },

  monotony: {
    title: 'Monotonia del carico',
    placement: 'end',
    size: 'md',
    description: (
      <Guide>
        <p className={styles.p}>
          La monotonia (Foster) misura quanto il carico è uguale giorno per giorno
          nella settimana: media dei carichi giornalieri divisa per la loro
          variabilità. Alta = tutti i giorni simili, poca alternanza.
        </p>
        <div className={styles.sectionTitle}>Come leggerlo</div>
        <Scale
          bands={[
            { dot: styles.dotGreen, range: `≤ ${MONOTONY_WARN_THRESHOLD.toFixed(1)}`, band: 'variazione sana' },
            { dot: styles.dotAmber, range: `> ${MONOTONY_WARN_THRESHOLD.toFixed(1)}`, band: 'troppo monotono' },
          ]}
        />
        <p className={styles.note}>
          Monotonia alta insieme a carico alto fa salire lo «strain» → più
          rischio. Alterna giorni duri e giorni leggeri per abbassarla.
        </p>
      </Guide>
    ),
  },

  strain: {
    title: 'Strain — carico settimanale × monotonia',
    placement: 'end',
    size: 'md',
    description: (
      <Guide>
        <p className={styles.p}>
          Lo strain (Foster) è il carico settimanale totale moltiplicato per la
          monotonia. Riassume in un solo numero la combinazione «tanto carico +
          sempre uguale», che è quella più legata a infortuni e malattie: due
          settimane con lo stesso carico totale hanno strain diverso se una
          alterna giorni duri e leggeri e l&apos;altra no.
        </p>
        <p className={styles.note}>
          Non ha una soglia fissa: si legge insieme a carico e monotonia. Sale
          quando il carico è alto <em>e</em> monotono; per abbassarlo alterna
          giorni duri e leggeri (meno monotonia) o riduci il volume.
        </p>
      </Guide>
    ),
  },

  delta: {
    title: 'Δ% — variazione vs settimana precedente',
    placement: 'end',
    size: 'md',
    description: (
      <Guide>
        <p className={styles.p}>
          Il Δ% confronta il carico di questa settimana con quello della
          precedente. Positivo = carico in aumento, negativo = in calo. Serve a
          cogliere i salti bruschi, in entrambe le direzioni.
        </p>
        <div className={styles.sectionTitle}>Come leggerlo</div>
        <Scale
          bands={[
            { dot: styles.dotGreen, range: `≤ ±${DELTA_WARN_PCT}%`, band: 'variazione contenuta' },
            { dot: styles.dotAmber, range: `±${DELTA_WARN_PCT}–${DELTA_ALERT_PCT}%`, band: 'attenzione' },
            { dot: styles.dotRed, range: `> ±${DELTA_ALERT_PCT}%`, band: 'salto brusco' },
          ]}
        />
        <p className={styles.note}>
          Aumenti troppo grandi caricano in fretta (rischio, vedi ACWR); cali
          netti possono essere uno scarico voluto o uno stop. Il Δ% richiede una
          settimana precedente con dati di carico.
        </p>
      </Guide>
    ),
  },

  confidenceBadge: {
    title: 'Confidenza del Recovery Index',
    placement: 'end',
    size: 'md',
    description: (
      <Guide>
        <p className={styles.p}>
          Il badge accanto al RI dice su <strong>quanti fattori di dato</strong> è
          calcolato quel Recovery Index, non se il valore è alto o basso. Un atleta
          che ha compilato solo il questionario Hooper ha un solo fattore: il suo RI
          è comunque valido (può anche essere 100), ma poggia su una base stretta.
        </p>
        <div className={styles.sectionTitle}>Come leggerlo</div>
        <Scale
          bands={[
            { dot: styles.dotRiGood, range: '3+ fattori', band: 'confidenza piena (nessun badge)' },
            { dot: styles.dotAmber, range: '2 fattori', band: 'PARZIALE' },
            { dot: styles.dotRed, range: '1 fattore', band: 'BASSA CONF.' },
          ]}
        />
        <p className={styles.note}>
          «BASSA CONF.» NON vuol dire recupero basso: vuol dire pochi dati. Per una
          lettura più affidabile servono più misure oltre all&apos;Hooper — CMJ, HRV,
          FC a riposo. I fattori sono Mentale (Hooper), Muscolare (CMJ), Cardio
          (HRV/FC) e Strain.
        </p>
      </Guide>
    ),
  },

  questionnaireCoverage: {
    title: 'Copertura questionari',
    placement: 'end',
    size: 'md',
    description: (
      <Guide>
        <p className={styles.p}>
          La copertura è la percentuale di atleti che hanno compilato il
          questionario nel periodo. Dice quanto ci si può fidare delle medie di
          squadra.
        </p>
        <div className={styles.sectionTitle}>Come leggerlo</div>
        <Scale
          bands={[
            { dot: styles.dotGreen, range: `≥ ${QUESTIONNAIRE_COVERAGE_BANDS.good}%`, band: 'buona' },
            { dot: styles.dotAmber, range: `${QUESTIONNAIRE_COVERAGE_BANDS.warn}–${QUESTIONNAIRE_COVERAGE_BANDS.good - 1}%`, band: 'parziale' },
            { dot: styles.dotRed, range: `< ${QUESTIONNAIRE_COVERAGE_BANDS.warn}%`, band: 'scarsa' },
          ]}
        />
        <p className={styles.note}>
          Con copertura bassa le medie di squadra si basano su pochi atleti e
          diventano poco rappresentative: sollecita chi non ha compilato.
        </p>
      </Guide>
    ),
  },
} satisfies Record<string, InfoDrawerValue>;
